import { Component, Input } from '@angular/core';
import { Router } from '@angular/router';
import { NbDialogRef } from '@nebular/theme';
import { UserRole } from '../../pages/customers/customers.type';

interface CreditLimitDetails {
  entityId?: number;
  currency?: string;
  owed?: number;
  limit?: number;
  projected?: number;
  /** Payment due date block: unpaid amounts of requests made before the due date. */
  unpaid?: Record<string, number>;
}

/**
 * Centered modal shown (in place of the generic error toast) when a credit-limit
 * gate blocks an action. Opened by the global ErrorHandlingInterceptor when the
 * error carries code ENTITY_CREDIT_LIMIT_EXCEEDED / ENTITY_CREDIT_LIMIT_REACHED.
 * "Check entity requests" deep-links to the Entity Requests report filtered to
 * the entity; "Dismiss" just closes.
 */
@Component({
  selector: 'ngx-credit-limit-modal',
  template: `
    <nb-card style="max-width: 460px; border-radius: 16px; overflow: hidden; margin: 0;">
      <div style="height: 5px; background: linear-gradient(90deg, #f87171, #e5426e);"></div>
      <nb-card-body style="text-align: center; padding: 26px 26px 8px;">
        <div
          style="width: 60px; height: 60px; border-radius: 50%; background: #fde4ea; color: #e5426e; display: inline-flex; align-items: center; justify-content: center; margin-bottom: 14px;"
        >
          <nb-icon icon="alert-circle-outline" style="font-size: 30px;"></nb-icon>
        </div>
        <h3 style="font-size: 19px; font-weight: 800; letter-spacing: -0.2px; margin: 0 0 8px;">
          {{ title }}
        </h3>
        <p
          style="font-size: 14px; line-height: 1.5; color: #5b6478; margin: 0 auto 18px; max-width: 380px;"
        >
          {{ message }}
        </p>

        <div
          *ngIf="canSeeFigures && details?.currency && details?.limit != null"
          style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;"
        >
          <div style="background: #f7f8fc; border-radius: 10px; padding: 10px 16px; min-width: 96px;">
            <div style="font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #8f9bb3;">
              Owed
            </div>
            <div style="font-size: 17px; font-weight: 800; margin-top: 2px;">
              {{ format(details?.owed) }} {{ details?.currency }}
            </div>
          </div>
          <div style="background: #f7f8fc; border-radius: 10px; padding: 10px 16px; min-width: 96px;">
            <div style="font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #8f9bb3;">
              Limit
            </div>
            <div style="font-size: 17px; font-weight: 800; margin-top: 2px;">
              {{ format(details?.limit) }} {{ details?.currency }}
            </div>
          </div>
          <div
            *ngIf="details?.projected != null"
            style="background: #f7f8fc; border-radius: 10px; padding: 10px 16px; min-width: 96px;"
          >
            <div style="font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #8f9bb3;">
              After this request
            </div>
            <div style="font-size: 17px; font-weight: 800; margin-top: 2px; color: #d3264b;">
              {{ format(details?.projected) }} {{ details?.currency }}
            </div>
          </div>
          <div
            *ngIf="overBy !== null"
            style="background: #f7f8fc; border-radius: 10px; padding: 10px 16px; min-width: 96px;"
          >
            <div style="font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #8f9bb3;">
              Over by
            </div>
            <div style="font-size: 17px; font-weight: 800; margin-top: 2px; color: #d3264b;">
              {{ format(overBy) }} {{ details?.currency }}
            </div>
          </div>
        </div>
        <div
          *ngIf="canSeeFigures && unpaidLines.length"
          style="display: flex; gap: 10px; justify-content: center; flex-wrap: wrap;"
        >
          <div
            *ngFor="let line of unpaidLines"
            style="background: #f7f8fc; border-radius: 10px; padding: 10px 16px; min-width: 96px;"
          >
            <div style="font-size: 10.5px; text-transform: uppercase; letter-spacing: 0.5px; color: #8f9bb3;">
              Unpaid
            </div>
            <div style="font-size: 17px; font-weight: 800; margin-top: 2px; color: #d3264b;">
              {{ format(line.amount) }} {{ line.currency }}
            </div>
          </div>
        </div>
      </nb-card-body>
      <nb-card-footer
        style="display: flex; gap: 10px; justify-content: flex-end; border-top: none; padding: 18px 22px 22px;"
      >
        <button nbButton status="basic" (click)="dismiss()">Dismiss</button>
        <button nbButton status="primary" (click)="checkEntityRequests()">
          Check entity requests
        </button>
      </nb-card-footer>
    </nb-card>
  `,
})
export class CreditLimitModalComponent {
  @Input() message = "This request would exceed the entity's credit limit.";
  @Input() details: CreditLimitDetails | null = null;
  @Input() title = 'Credit limit reached';

  get unpaidLines(): { currency: string; amount: number }[] {
    return Object.entries(this.details?.unpaid || {}).map(([currency, amount]) => ({
      currency,
      amount: Number(amount),
    }));
  }

  /**
   * Credit figures (owed/limit) are only shown to the roles allowed to see an
   * entity's credit picture — super-admin, accountant, entity owner — matching
   * the account card. Other staff who hit the gate still get the message and the
   * "Check entity requests" action, just not the numbers.
   */
  get canSeeFigures(): boolean {
    let role: string | undefined;
    try {
      role = JSON.parse(localStorage.getItem('user') || '{}')?.role;
    } catch {
      role = undefined;
    }
    return (
      role === UserRole.SUPER_ADMIN ||
      role === UserRole.ACCOUNTANT ||
      role === UserRole.ENTITY_OWNER
    );
  }

  constructor(
    private ref: NbDialogRef<CreditLimitModalComponent>,
    private router: Router,
  ) {}

  get overBy(): number | null {
    if (!this.details || this.details.limit == null) {
      return null;
    }
    // For the "would exceed" case the block is caused by this request pushing the
    // projected total over the limit; fall back to current owed for the
    // "already at/over the limit" case (no projected supplied).
    const base = this.details.projected ?? this.details.owed;
    if (base == null) {
      return null;
    }
    const diff = Number(base) - Number(this.details.limit);
    return diff > 0 ? diff : 0;
  }

  format(value: number | null | undefined): string {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  checkEntityRequests(): void {
    const entityId = this.details?.entityId;
    this.ref.close();
    this.router.navigate(
      ['/pages/reports/entity-requests'],
      entityId ? { queryParams: { entityId } } : {},
    );
  }

  dismiss(): void {
    this.ref.close();
  }
}
